import React, { useRef, useState, useEffect } from 'react';
import { ClickAwayListener } from '@material-ui/core';
import { useTranslation } from '~/dependencies';
import { useSelector } from 'react-redux';
import {
  MessagesIcon,
  SMSIcon,
  TeamsIcon,
  WhatsappIcon,
  ArrowUpIcon,
} from '~/assets/svgIcons';
import { colors } from '~/layout';
import { ChannelTypeEnum } from '~/enums/conversations';
import { RootState } from '~/store/config';
import {
  ArrowIcon,
  Button,
  Container,
  Icon,
  Menu,
  MenuItem,
  MenuList,
} from './styles';

type ChannelType = {
  id: number;
  type: ChannelTypeEnum | number;
  name: string;
  icon: string;
  iconColor?: string;
  enabled: boolean;
};

type ChannelMenuProps = {
  onChange: (channelId: ChannelTypeEnum) => void;
  disabled?: boolean;
  activeChannelType?: ChannelTypeEnum | number;
};

export default function ChannelMenu({
  onChange,
  disabled,
  activeChannelType,
}: ChannelMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef(null);

  const { isWhatsappEnabled, isMmsEnabled } = useSelector(
    (state: RootState) => state.accountInfo.featureFlags
  );

  const channelOptions: ChannelType[] = [
    {
      id: 0,
      name: t('screens.conversation.allChats'),
      type: 0,
      icon: MessagesIcon,
      iconColor: colors.darkGray11,
      enabled: true,
    },
    {
      id: 1,
      name: isMmsEnabled ? 'SMS/MMS' : 'SMS Chats',
      type: ChannelTypeEnum.SMS,
      icon: SMSIcon,
      enabled: true,
    },
    {
      id: 2,
      name: 'WhatsApp',
      type: ChannelTypeEnum.WHATSAPP,
      icon: WhatsappIcon,
      enabled: isWhatsappEnabled,
    },
    {
      id: 3,
      name: 'Internal',
      type: ChannelTypeEnum.MMS,
      icon: TeamsIcon,
      enabled: false,
    },
  ];

  const enabledChannelOptions = channelOptions.filter(
    channel => channel.enabled
  );

  const findChannelByType = (type?: ChannelTypeEnum | number) =>
    enabledChannelOptions.find(opt => opt.type === type) ??
    enabledChannelOptions[0];

  // STATES
  const [open, setOpen] = useState<boolean>(false);
  const [curSelected, setCurSelected] = useState<ChannelType>(() =>
    findChannelByType(activeChannelType)
  );

  useEffect(() => {
    setCurSelected(findChannelByType(activeChannelType));
  }, [activeChannelType, isWhatsappEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelection = (channel: ChannelType) => {
    setOpen(false);
    setCurSelected(channel);

    if (onChange) {
      onChange(channel.type);
    }
  };

  const handleOpen = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Container>
        <Menu ref={menuRef}>
          <Button
            onClick={handleOpen}
            open={open}
            data-test-id="conversation-channel-selector">
            <Icon src={curSelected.icon} color={curSelected.iconColor} />
            <span style={{ fontWeight: 'bold', color: colors.darkGray11 }}>
              {curSelected.name}
            </span>
            <ArrowIcon open={open} src={ArrowUpIcon} />
          </Button>

          <MenuList open={open}>
            {enabledChannelOptions.map(e => (
              <li key={String(e.id)}>
                <MenuItem
                  role="button"
                  data-test-id={`channel-option-${e.name
                    .toLowerCase()
                    .replace(/\s+/g, '-')}`}
                  onClick={() => handleSelection(e)}>
                  <Icon src={e.icon} color={e.iconColor} />
                  <span
                    style={{
                      fontWeight: curSelected.id === e.id ? 'bold' : 'normal',
                      color: colors.darkGray11,
                    }}>
                    {e.name}
                  </span>
                </MenuItem>
              </li>
            ))}
          </MenuList>
        </Menu>
      </Container>
    </ClickAwayListener>
  );
}

ChannelMenu.defaultProps = {
  disabled: false,
  activeChannelType: undefined,
};
